'use client';

import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { isLeadFieldRequired, stripFieldLabelAsterisk } from '@/lib/lead-forms';
import type { LeadFormStateDistrictDropdown } from '@/types/lead-forms';
import { cn } from '@/utils/cn';

const selectTriggerClassName =
  'contact-enquiry-field h-11 rounded-none border-0 border-b border-black/25 bg-transparent px-0 text-sm text-black shadow-none focus:ring-0 focus-visible:ring-0 data-[placeholder]:text-[#4c4c4c] [&>span]:text-left';
const searchableSelectContentClass =
  'max-h-72 w-[var(--radix-select-trigger-width)] rounded-md border border-black/10 bg-white text-black shadow-xl';

interface WCLGeoOption {
  id: string;
  name: string;
}

/** POST to a WCL geo proxy and hand back the option array (empty on any failure). */
function fetchWclGeoOptions(
  endpoint: string,
  body: Record<string, unknown>,
  onData: (options: WCLGeoOption[]) => void,
): () => void {
  let active = true;
  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then((response) => (response.ok ? response.json() : []))
    .then((data) => {
      if (active && Array.isArray(data)) onData(data as WCLGeoOption[]);
    })
    .catch(() => {});
  return () => {
    active = false;
  };
}

/** Placeholder shown in the district menu when no options match. */
function districtEmptyStateText(hasStateValue: boolean, districtCount: number): string {
  if (!hasStateValue) return 'Select state first';
  return districtCount === 0 ? 'Loading districts…' : 'No matches found';
}

export function LeadFormSelectSearchInput({
  value,
  onChange,
  onClear,
  placeholder,
  'aria-label': ariaLabel,
  clearAriaLabel = 'Clear',
}: {
  value: string;
  onChange: (next: string) => void;
  onClear?: () => void;
  placeholder: string;
  'aria-label': string;
  clearAriaLabel?: string;
}) {
  const showClear = Boolean(value || onClear);
  return (
    <div className="sticky -top-1 z-10 border-b border-black/10 bg-white p-2">
      <div className="relative">
        <Input
          type="text"
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 rounded-md border border-black/15 bg-white px-3 pr-8 text-sm text-black shadow-none placeholder:!text-black/45 focus-visible:border-[#ed1c22] focus-visible:ring-0"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
        {showClear ? (
          <button
            type="button"
            aria-label={clearAriaLabel}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-[#ed1c22] hover:bg-red-50"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChange('');
              onClear?.();
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function StateSelectField({
  stateId,
  stateLabel,
  required,
  stateValue,
  states,
  filteredStates,
  stateSearch,
  setStateSearch,
  setDistrictSearch,
  onStateChange,
  onDistrictChange,
  stateError,
  selectContentClassName,
}: {
  stateId: string;
  stateLabel: string;
  required: boolean;
  stateValue: string;
  states: WCLGeoOption[];
  filteredStates: WCLGeoOption[];
  stateSearch: string;
  setStateSearch: (next: string) => void;
  setDistrictSearch: (next: string) => void;
  onStateChange: (next: string) => void;
  onDistrictChange: (next: string) => void;
  stateError?: string;
  selectContentClassName?: string;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor={stateId} className="text-sm font-semibold text-black/80">
        {stateLabel}
        {required ? <span className="ml-0.5 text-[#E31E24]">*</span> : null}
      </Label>
      <Select
        key={`state-${stateValue}-${states.length}`}
        value={stateValue || undefined}
        onValueChange={(value) => {
          onStateChange(value);
          onDistrictChange('');
          setStateSearch('');
          setDistrictSearch('');
        }}
        onOpenChange={(open) => {
          if (open) setStateSearch('');
        }}
      >
        <SelectTrigger
          id={stateId}
          className={selectTriggerClassName}
          aria-required={required || undefined}
          aria-invalid={Boolean(stateError)}
        >
          <SelectValue placeholder={stateLabel} />
        </SelectTrigger>
        <SelectContent className={cn(searchableSelectContentClass, selectContentClassName)}>
          <LeadFormSelectSearchInput
            value={stateSearch}
            onChange={setStateSearch}
            onClear={
              stateValue
                ? () => {
                    onStateChange('');
                    onDistrictChange('');
                    setDistrictSearch('');
                  }
                : undefined
            }
            clearAriaLabel="Clear state and district"
            placeholder="Search states…"
            aria-label="Search states"
          />
          {filteredStates.length > 0 ? (
            filteredStates.map((state) => (
              <SelectItem key={state.id} value={state.id}>
                {state.name}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="__no-states" disabled>
              {states.length === 0 ? 'Loading states…' : 'No matches found'}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      {stateError ? (
        <p className="text-xs text-[#F0182B]" role="alert">
          {stateError}
        </p>
      ) : null}
    </div>
  );
}

function DistrictSelectField({
  districtId,
  districtLabel,
  required,
  stateValue,
  districtValue,
  districts,
  filteredDistricts,
  districtSearch,
  setDistrictSearch,
  onDistrictChange,
  districtError,
  selectContentClassName,
}: {
  districtId: string;
  districtLabel: string;
  required: boolean;
  stateValue: string;
  districtValue: string;
  districts: WCLGeoOption[];
  filteredDistricts: WCLGeoOption[];
  districtSearch: string;
  setDistrictSearch: (next: string) => void;
  onDistrictChange: (next: string) => void;
  districtError?: string;
  selectContentClassName?: string;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor={districtId} className="text-sm font-semibold text-black/80">
        {districtLabel}
        {required ? <span className="ml-0.5 text-[#E31E24]">*</span> : null}
      </Label>
      <Select
        key={`district-${districtValue}-${districts.length}`}
        value={districtValue || undefined}
        onValueChange={(value) => {
          onDistrictChange(value);
          setDistrictSearch('');
        }}
        disabled={!stateValue}
        onOpenChange={(open) => {
          if (open) setDistrictSearch('');
        }}
      >
        <SelectTrigger
          id={districtId}
          className={selectTriggerClassName}
          aria-required={required || undefined}
          aria-invalid={Boolean(districtError)}
        >
          <SelectValue placeholder={districtLabel} />
        </SelectTrigger>
        <SelectContent className={cn(searchableSelectContentClass, selectContentClassName)}>
          <LeadFormSelectSearchInput
            value={districtSearch}
            onChange={setDistrictSearch}
            onClear={districtValue ? () => onDistrictChange('') : undefined}
            clearAriaLabel="Clear district"
            placeholder="Search districts…"
            aria-label="Search districts"
          />
          {filteredDistricts.length > 0 ? (
            filteredDistricts.map((district) => (
              <SelectItem key={district.id} value={district.id}>
                {district.name}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="__no-districts" disabled>
              {districtEmptyStateText(Boolean(stateValue), districts.length)}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      {districtError ? (
        <p className="text-xs text-[#F0182B]" role="alert">
          {districtError}
        </p>
      ) : null}
    </div>
  );
}

export default function LeadFormStateDistrictField({
  field,
  stateValue,
  districtValue,
  onStateChange,
  onDistrictChange,
  stateError,
  districtError,
  selectContentClassName,
}: {
  field: LeadFormStateDistrictDropdown;
  stateValue: string;
  districtValue: string;
  onStateChange: (next: string) => void;
  onDistrictChange: (next: string) => void;
  stateError?: string;
  districtError?: string;
  selectContentClassName?: string;
}) {
  const stateId = field.stateFieldId || `state-${field.id}`;
  const districtId = field.districtFieldId || `district-${field.id}`;
  const hasState = Boolean(field.stateFieldLabel);
  const hasDistrict = Boolean(field.districtFieldLabel);
  const required = isLeadFieldRequired(field);
  const stateLabel = stripFieldLabelAsterisk(field.stateFieldLabel);
  const districtLabel = stripFieldLabelAsterisk(field.districtFieldLabel);

  const [states, setStates] = useState<WCLGeoOption[]>([]);
  const [districts, setDistricts] = useState<WCLGeoOption[]>([]);
  const [stateSearch, setStateSearch] = useState('');
  const [districtSearch, setDistrictSearch] = useState('');

  useEffect(() => {
    if (!hasState && !hasDistrict) return;
    return fetchWclGeoOptions('/api/wcl/states', { searchParam: '' }, setStates);
  }, [hasState, hasDistrict]);

  useEffect(() => {
    if (!hasDistrict || !stateValue) {
      setDistricts([]);
      return;
    }
    return fetchWclGeoOptions(
      '/api/wcl/districts',
      { searchParam: '', stateId: stateValue },
      setDistricts,
    );
  }, [hasDistrict, stateValue]);

  const filteredStates = useMemo(() => {
    const term = stateSearch.trim().toLowerCase();
    if (!term) return states;
    return states.filter((state) => state.name.toLowerCase().includes(term));
  }, [states, stateSearch]);

  const filteredDistricts = useMemo(() => {
    const term = districtSearch.trim().toLowerCase();
    if (!term) return districts;
    return districts.filter((district) => district.name.toLowerCase().includes(term));
  }, [districts, districtSearch]);

  if (!hasState && !hasDistrict) return null;

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {hasState ? (
        <StateSelectField
          stateId={stateId}
          stateLabel={stateLabel}
          required={required}
          stateValue={stateValue}
          states={states}
          filteredStates={filteredStates}
          stateSearch={stateSearch}
          setStateSearch={setStateSearch}
          setDistrictSearch={setDistrictSearch}
          onStateChange={onStateChange}
          onDistrictChange={onDistrictChange}
          stateError={stateError}
          selectContentClassName={selectContentClassName}
        />
      ) : null}

      {hasDistrict ? (
        <DistrictSelectField
          districtId={districtId}
          districtLabel={districtLabel}
          required={required}
          stateValue={stateValue}
          districtValue={districtValue}
          districts={districts}
          filteredDistricts={filteredDistricts}
          districtSearch={districtSearch}
          setDistrictSearch={setDistrictSearch}
          onDistrictChange={onDistrictChange}
          districtError={districtError}
          selectContentClassName={selectContentClassName}
        />
      ) : null}
    </div>
  );
}
